import React, { useContext } from "react"
import useSSR from "use-ssr"
import {
	Flex,
	Accordion,
	useAccordionActiveAccordion,
	Button,
	breakpoints,
} from "@capioweb/ui-components"
import {
	Property,
	StringProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { BlockCommonLayout, TwoColumnGridLayout } from "../../utils/layout"
import {
	OurOfferAccordionItem,
	OurOfferAccordionBaseBlockItem,
} from "./OurOfferAccordionItem"
import { AnchorLinkContext, LocalesContext } from "../../context"
import { useSetCols, useHandleHashChange } from "../../hooks"

type CapioNexusOurOfferContentAreaProperty = Property<
	OurOfferAccordionBaseBlockItem[]
>
interface CapioNexusOurOfferSectionBlockProps {
	heading: StringProperty
	content: CapioNexusOurOfferContentAreaProperty
	anchorId?: StringProperty
}

const CapioNexusOurOfferSectionBlock: React.FC<CapioNexusOurOfferSectionBlockProps> =
	({ content, ...rest }) => {
		const { isServer } = useSSR()
		const { translate, translationKeys } = React.useContext(LocalesContext)
		const anchorLinkContext = useContext(AnchorLinkContext)
		const maxItemsPerCol = 6
		const [collapsed, setCollapsed] = React.useState(true)
		const { cols } = useSetCols(content?.value ?? null)

		// Listen for hash on popState and update active accordion ID
		const { activeId, setActiveId } = useAccordionActiveAccordion(null)
		useHandleHashChange(setActiveId)

		React.useEffect(() => {
			if (isServer) return

			if (window.innerWidth > breakpoints.l && content?.value) {
				if (content.value.length <= maxItemsPerCol * 2) {
					setCollapsed(false)
				}
			}
		}, [setCollapsed])

		React.useEffect(() => {
			setActiveId(anchorLinkContext.activeId)
		}, [])

		return (
			<BlockCommonLayout id={rest.anchorId?.value || null} {...rest}>
				{content && (
					<Flex w={1} flexDirection="column" alignItems="center">
						<TwoColumnGridLayout
							items={cols.map((col, i) => (
								<Accordion key={`acc-${i}`} activeId={activeId} controlled>
									{col.map((item, colI) => (
										<OurOfferAccordionItem
											{...item}
											setActiveId={setActiveId}
											activeId={activeId}
											hidden={
												collapsed && colI + 1 > maxItemsPerCol ? true : false
											}
											key={colI}
										/>
									))}
								</Accordion>
							))}
						/>
						<Button
							display={collapsed ? "inline-flex" : "none"}
							variant="ghost"
							textColor="navy"
							icon="plus"
							onClick={() => setCollapsed(false)}
							mt={{ _: 2, l: 5 }}
						>
							{translate(translationKeys.showMore)}
						</Button>
					</Flex>
				)}
			</BlockCommonLayout>
		)
	}

export { CapioNexusOurOfferSectionBlock }
